import React, { useEffect, useRef, useState } from "react";
import { Close, Hearing, VideocamOutlined } from "@mui/icons-material";
import { CLINICAL_DEVICE_IDS } from "../../features/deviceWebSocket/deviceMessageProtocol";
import ClinicalAudioEqualizer from "./ClinicalAudioEqualizer";
import "./ClinicalDeviceStreamView.css";

const DEVICE_LABELS = Object.freeze({
  [CLINICAL_DEVICE_IDS.STETHOSCOPE]: "Stethoscope",
  [CLINICAL_DEVICE_IDS.EAR_CAMERA]: "Ear camera",
  [CLINICAL_DEVICE_IDS.EXAM_CAMERA]: "Exam camera",
});

const STATE_LABELS = Object.freeze({
  starting: "Starting kiosk stream",
  ready: "Connecting to kiosk",
  connecting: "Establishing secure media",
  streaming: "Live",
  reconnecting: "Reconnecting media",
  stopping: "Stopping",
  failed: "Stream unavailable",
});

const DEFAULT_VIDEO_SIZE = Object.freeze({ width: 16, height: 9 });
const MAX_VIDEO_HEIGHT_VH = 65;

function ClinicalDeviceStreamView({
  deviceId,
  stream,
  state,
  message,
  onStop,
}) {
  const videoRef = useRef(null);
  const isAudio = deviceId === CLINICAL_DEVICE_IDS.STETHOSCOPE;
  const [videoSize, setVideoSize] = useState(DEFAULT_VIDEO_SIZE);

  useEffect(() => {
    if (isAudio) return undefined;
    const mediaElement = videoRef.current;
    if (!mediaElement) return undefined;
    mediaElement.srcObject = stream || null;
    if (stream) {
      Promise.resolve(mediaElement.play?.()).catch(() => undefined);
    }
    return () => {
      if (mediaElement.srcObject === stream) mediaElement.srcObject = null;
    };
  }, [isAudio, stream]);

  useEffect(() => {
    setVideoSize(DEFAULT_VIDEO_SIZE);
  }, [deviceId, stream]);

  const syncVideoSize = (event) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (!videoWidth || !videoHeight) return;

    setVideoSize((currentSize) => (
      currentSize.width === videoWidth && currentSize.height === videoHeight
        ? currentSize
        : { width: videoWidth, height: videoHeight }
    ));
  };

  if (!deviceId) return null;

  const videoLayoutStyle = isAudio ? undefined : {
    "--clinical-stream-aspect-ratio": `${videoSize.width} / ${videoSize.height}`,
    "--clinical-stream-max-width": `${MAX_VIDEO_HEIGHT_VH * (videoSize.width / videoSize.height)}vh`,
  };

  return (
    <section
      className={`clinical-stream-view${isAudio ? "" : " clinical-stream-view--video"}`}
      style={videoLayoutStyle}
      aria-label={`${DEVICE_LABELS[deviceId]} stream`}
    >
      <header className="clinical-stream-view__header">
        <div>
          {isAudio ? <Hearing /> : <VideocamOutlined />}
          <strong>{DEVICE_LABELS[deviceId]}</strong>
          <span className={`clinical-stream-view__state clinical-stream-view__state--${state || "starting"}`}>
            {STATE_LABELS[state] || "Preparing"}
          </span>
        </div>
        <button type="button" onClick={onStop} aria-label={`Stop ${DEVICE_LABELS[deviceId]} stream`}>
          <Close />
          <span>Stop</span>
        </button>
      </header>

      {isAudio ? (
        <ClinicalAudioEqualizer stream={stream} streamState={state} />
      ) : (
        <div className="clinical-stream-view__video-wrap">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={syncVideoSize}
            onResize={syncVideoSize}
          />
          {!stream && (
            <div className="clinical-stream-view__waiting">
              <span className="clinical-stream-view__spinner" />
              <strong>{STATE_LABELS[state] || "Waiting for kiosk media"}</strong>
              {message && <span>{message}</span>}
            </div>
          )}
        </div>
      )}

      {state === "failed" && message && (
        <div className="clinical-stream-view__error" role="alert">{message}</div>
      )}
    </section>
  );
}

export default ClinicalDeviceStreamView;
